import {
  TOrganization,
  TOrganizationActiveContract,
  TOrganizationDashboard,
  TOrganizationDashboardOld,
  TOrganizationReport,
  TTeamConcerns,
  TTeamConcernsRange,
} from '@/types/Organization.types';
import { api, TCustomAxiosResponse } from '..';
import { apiPaths } from '@/services/api/paths';
import { TUser, TUserAffiliation } from '@/types/User.types';
import { TMemberCare } from '@/types/MemberCare.types';
import { INFINITE_SCROLL_CARDS_PER_PAGE } from '@/constants';
import { TPeerSupportDashboard, TPeerSupportTeamMemberInfo } from '@/types/PeerSupport.types';
import type { InviteUsersResultDto, RevokeInvitationResponseDto, ResolveInvitationRevokeDto, UpdateOrganizationDto } from '@/types/generated/schemas';

export const myOrganizationGet = async (): Promise<TOrganization> => {
  const { data } = await api.get<TCustomAxiosResponse<TOrganization>>(apiPaths.organization.my);
  const organization = data.data;

  return organization;
};

export const myOrganizationDashboardGet = async (): Promise<TOrganizationDashboard> => {
  const { data } = await api.get<TCustomAxiosResponse<TOrganizationDashboard>>(apiPaths.organization.dashboard);
  const organizationDash = data.data;

  return organizationDash;
};

export const myOrganizationActiveContractGet = async (): Promise<TOrganizationActiveContract> => {
  const { data } = await api.get<TCustomAxiosResponse<TOrganizationActiveContract>>(
    apiPaths.organization.activeContract,
  );
  const organizationContract = data.data;

  return organizationContract;
};

export const peerSupportEngagementGet = async (monthDate: string): Promise<TPeerSupportDashboard> => {
  const [year, month] = monthDate.split('-');
  const { data } = await api.get<TCustomAxiosResponse<TPeerSupportDashboard>>(
    `${apiPaths.organization.peerSupportEngagement}${monthDate ? `?month=${month}&year=${year}` : ''}`,
  );

  return data.data;
};

export const teamConcernsGet = async (range: TTeamConcernsRange): Promise<TTeamConcerns> => {
  const { data } = await api.get<TCustomAxiosResponse<TTeamConcerns>>(apiPaths.organization.teamConcerns, {
    params: { range },
  });

  return data.data;
};

export const myOrganizationDashboardGetOld = async (): Promise<TOrganizationDashboardOld> => {
  const { data } = await api.get<TCustomAxiosResponse<TOrganizationDashboardOld>>(apiPaths.organization.dashboardOld);
  const organizationDash = data.data;

  return organizationDash;
};

export const updateOrganizationByIdPut = async (organizationId: string, orgInfo: UpdateOrganizationDto) => {
  const { data } = await api.put<TCustomAxiosResponse<TOrganization>>(apiPaths.organization.byId(organizationId), {
    ...orgInfo,
  });
  const updatedOrganization = data.data;
  return updatedOrganization;
};

export const inviteUsersToOrganizationPost = async (
  users: {
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
    userAffiliation?: string;
    isPeerSupport?: boolean;
    isPeerSupportLead?: boolean;
    certificateId?: string;
    validationStartDate?: string;
    validationEndDate?: string;
  }[],
) => {
  const usersWithMappedFields = users.map((user) => ({
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    ...(user.userAffiliation && { userAffiliation: user.userAffiliation }),
    ...(user.isPeerSupport !== undefined && { isPeerSupport: user.isPeerSupport }),
    ...(user.isPeerSupportLead !== undefined && { isPeerSupportLead: user.isPeerSupportLead }),
    ...(user.certificateId && { certificateId: user.certificateId }),
    ...(user.validationStartDate && { validationStartDate: user.validationStartDate }),
    ...(user.validationEndDate && { validationEndDate: user.validationEndDate }),
  }));

  const { data } = await api.post<TCustomAxiosResponse<InviteUsersResultDto>>(apiPaths.organization.inviteUsers, {
    users: usersWithMappedFields,
  });

  return data.data;
};

export const reinviteUsersToOrganizationPost = async (organizationId: string, userId: string) => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.organization.reinvite(organizationId, userId),
  );

  return data.data;
};

export const revokeInvitationPost = async (organizationId: string, userId: string) => {
  const { data } = await api.post<RevokeInvitationResponseDto>(
    apiPaths.organization.revokeInvitation(organizationId, userId),
  );
  return data;
};

export const resolveInvitationRevokePost = async (
  organizationId: string,
  userId: string,
  decision: ResolveInvitationRevokeDto,
) => {
  const { data } = await api.post<RevokeInvitationResponseDto>(
    apiPaths.organization.resolveInvitationRevoke(organizationId, userId),
    decision,
  );
  return data;
};

export const uploadOrganizationLogoPost = async (file: File): Promise<TOrganization> => {
  const formData = new FormData();
  formData.append('file', new File([file], file.name));

  const { data } = await api.post<TCustomAxiosResponse<TOrganization>>(apiPaths.organization.uploadLogo, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  const organization = data.data;
  return organization;
};

export const organizationMembersForAdminGet = async (
  page: number,
  _: string,
  searchFilterText?: string,
): Promise<TCustomAxiosResponse<TUser[]>> => {
  const { data } = await api.get(apiPaths.organization.members, { params: { page, search: searchFilterText } });

  return data;
};

export const removeMemberFromOrganization = async (
  orgMemberId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Promise<TCustomAxiosResponse<any>> => {
  const { data } = await api.delete(apiPaths.organization.employeeById(orgMemberId));
  return data;
};

export const getMemberCareFromMyOrgById = async (memberCareId: string): Promise<TMemberCare> => {
  const { data } = await api.get(apiPaths.organization.memberCareById(memberCareId));

  const memberCare = data.data as TMemberCare;

  return memberCare;
};

export const getPeerSupportFromMyOrgById = async (peerSupportId: string): Promise<TPeerSupportTeamMemberInfo> => {
  const { data } = await api.get(apiPaths.organization.peerSupportMemberById(peerSupportId));

  const peerSupport = data.data as TPeerSupportTeamMemberInfo;

  return peerSupport;
};

export const getMyOrganizationPeerSupportMembers = async (
  page: number,
  limit = INFINITE_SCROLL_CARDS_PER_PAGE,
  starredFirst = false,
): Promise<TCustomAxiosResponse<TPeerSupportTeamMemberInfo[]>> => {
  const { data } = await api.get(apiPaths.organization.peerSupportMembers, {
    params: { page, limit, ...(starredFirst ? { starredFirst: true } : {}) },
  });

  return data;
};

export const updateUserAffiliationPatch = async (
  organizationId: string,
  userId: string,
  newAffiliation: TUserAffiliation,
): Promise<{
  success: boolean;
  message: string;
}> => {
  const { data } = await api.patch<
    TCustomAxiosResponse<{
      success: boolean;
      message: string;
    }>
  >(apiPaths.organization.userAffiliation(organizationId, userId), {
    userAffiliation: newAffiliation.toUpperCase(),
  });

  return data.data;
};

export const assignPeerSupportLeadPost = async (organizationId: string, userId: string) => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.organization.assignPeerSupportLead(organizationId, userId),
  );

  return data.data;
};

export const revokePeerSupportLeadPost = async (organizationId: string, userId: string) => {
  const { data } = await api.post<TCustomAxiosResponse<unknown>>(
    apiPaths.organization.revokePeerSupportLead(organizationId, userId),
  );

  return data.data;
};

export const getMyOrganizationPeerSupportLeads = async (
  page: number,
  limit = INFINITE_SCROLL_CARDS_PER_PAGE,
): Promise<TCustomAxiosResponse<TPeerSupportTeamMemberInfo[]>> => {
  const { data } = await api.get(apiPaths.organization.peerSupportLeads, { params: { page, limit } });

  return data;
};

export const getMyOrganizationReport = async (year?: number, month?: number): Promise<TOrganizationReport> => {
  const urlParams = year && month ? `?year=${year}&month=${month}` : '';

  const { data } = await api.get(`${apiPaths.organization.reports}${urlParams}`);

  return data;
};
